
import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { getIconUrl } from "@/utils/iconUtils";

interface NodeData extends Record<string, unknown> {
  name: string;
  componentTypeId: string;
  title?: string;
  uuid: string;
  selectedComponentId?: string;
  selectedComponentName?: string;
  componentTypeName?: string;
  componentTypeIcon?: string;
  icon?: string;
  codeChanges7Days?: number;
  codeChanges30Days?: number;
  deploys7Days?: number;
  viewMode?: 'padrao' | 'codigo' | 'deploy';
  gradientMin?: number;
  gradientMax?: number;
  deployGradientMin?: number;
  deployGradientMax?: number;
  isSelected?: boolean;
}

export function ComponentNode({ data, isConnectable, selected }: { data: NodeData, isConnectable: boolean, selected?: boolean }) {
  const [isDebugMode, setIsDebugMode] = useState(false);
  
  // Debug: verificar se os dados chegaram
  console.log('🎨 [ComponentNode] Rendering with data:', {
    name: data.name,
    componentTypeName: data.componentTypeName,
    componentTypeIcon: data.componentTypeIcon,
    selectedComponentName: data.selectedComponentName,
    selectedComponentId: data.selectedComponentId
  });
  
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const debugParam = urlParams.get('debug');
    
    if (debugParam === 'true') {
      setIsDebugMode(true);
    } else if (window.location.href.includes('&debug=true')) {
      setIsDebugMode(true);
    } else {
      setIsDebugMode(false);
    }
  }, []);
  
  // Usar componentTypeName se disponível, senão usar name como fallback
  const displayName = data.componentTypeName || data.name;
  const displayTitle = data.selectedComponentId && data.selectedComponentId !== 'novo' 
    ? data.selectedComponentName 
    : data.title;
  // Usar componentTypeIcon se disponível, senão usar icon como fallback
  const iconToUse = data.componentTypeIcon || data.icon;
  const iconPath = getIconUrl(iconToUse);

  // Calcular cor do background e intensidade baseado na visão
  const getBackgroundAndTextColors = () => {
    // Visão Código
    if (data.viewMode === 'codigo' && data.codeChanges7Days !== undefined && data.codeChanges7Days !== null && data.codeChanges7Days > 0) {
      const { gradientMin = 0, gradientMax = 1 } = data;
      
      // Evitar divisão por zero
      if (gradientMax === gradientMin) {
        return { backgroundColor: 'bg-white', textColor: 'text-gray-800', iconColor: 'text-blue-600' };
      }
      
      // Calcular intensidade (0 a 1)
      const intensity = (data.codeChanges7Days - gradientMin) / (gradientMax - gradientMin);
      
      // Mapear intensidade para classes de verde do Tailwind
      if (intensity >= 0.8) return { backgroundColor: 'bg-green-600', textColor: 'text-white', iconColor: 'text-white' };
      if (intensity >= 0.6) return { backgroundColor: 'bg-green-500', textColor: 'text-white', iconColor: 'text-white' };
      if (intensity >= 0.4) return { backgroundColor: 'bg-green-400', textColor: 'text-gray-800', iconColor: 'text-blue-600' };
      if (intensity >= 0.2) return { backgroundColor: 'bg-green-300', textColor: 'text-gray-800', iconColor: 'text-blue-600' };
      return { backgroundColor: 'bg-green-200', textColor: 'text-gray-800', iconColor: 'text-blue-600' };
    }

    // Visão Deploy
    if (data.viewMode === 'deploy' && data.deploys7Days !== undefined && data.deploys7Days !== null && data.deploys7Days > 0) {
      const { deployGradientMin = 0, deployGradientMax = 1 } = data;
      
      // Evitar divisão por zero
      if (deployGradientMax === deployGradientMin) {
        return { backgroundColor: 'bg-white', textColor: 'text-gray-800', iconColor: 'text-blue-600' };
      }
      
      // Calcular intensidade (0 a 1)
      const intensity = (data.deploys7Days - deployGradientMin) / (deployGradientMax - deployGradientMin);
      
      // Mapear intensidade para classes de azul do Tailwind
      if (intensity >= 0.8) return { backgroundColor: 'bg-blue-600', textColor: 'text-white', iconColor: 'text-white' };
      if (intensity >= 0.6) return { backgroundColor: 'bg-blue-500', textColor: 'text-white', iconColor: 'text-white' };
      if (intensity >= 0.4) return { backgroundColor: 'bg-blue-400', textColor: 'text-gray-800', iconColor: 'text-blue-600' };
      if (intensity >= 0.2) return { backgroundColor: 'bg-blue-300', textColor: 'text-gray-800', iconColor: 'text-blue-600' };
      return { backgroundColor: 'bg-blue-200', textColor: 'text-gray-800', iconColor: 'text-blue-600' };
    }
    
    // Cores padrão para visão padrão ou nós sem dados ou com valores = 0
    return { backgroundColor: 'bg-white', textColor: 'text-gray-800', iconColor: 'text-blue-600' };
  };

  const { backgroundColor, textColor, iconColor } = getBackgroundAndTextColors();
  
  // Adicionar estilo de borda quando selecionado
  const borderStyle = selected ? 'border-2 border-blue-500' : 'border border-gray-200';

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className={`flex flex-col items-center justify-center p-4 shadow-lg rounded-xl ${borderStyle} w-[144px] h-[144px] relative hover:shadow-xl transition-all duration-200 ${backgroundColor}`}>
          {/* Icon */}
          <div className={`mb-3 ${iconColor}`}>
            {iconPath ? (
              <img 
                src={iconPath} 
                alt="Component Icon" 
                className={`w-8 h-8 ${iconColor === 'text-white' ? 'filter brightness-0 invert' : ''}`}
                onError={(e) => {
                  console.error(`Falha ao carregar ícone: ${iconToUse}`, 'Tentando usar:', iconPath);
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement?.classList.add('fallback-icon');
                }}
              />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="0.5">
                <path d="M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7zm2.85 11.1l-.85.6V16h-4v-2.3l-.85-.6A4.997 4.997 0 0 1 7 9c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.63-.8 3.16-2.15 4.1z"/>
              </svg>
            )}
          </div>
          
          {/* Component Name */}
          <div className={`text-sm font-semibold text-center mb-1 leading-tight ${textColor}`}>{displayName}</div>
          
          {/* Component Title */}
          {displayTitle && (
            <div className={`text-xs text-center mt-1 px-2 leading-tight ${textColor === 'text-white' ? 'text-white opacity-90' : 'text-gray-600'}`}>{displayTitle}</div>
          )}
          
          {/* UUID em modo dev */}
          {(process.env.NODE_ENV === 'development' || isDebugMode) && (
            <div className="absolute bottom-1 right-1 text-[7px] text-gray-400" title={data.uuid}>
              {data.uuid.substring(0, 6)}...
            </div>
          )}

          {/* Handles para conexões */}
          <Handle
            type="target"
            position={Position.Top}
            isConnectable={isConnectable}
            className="w-3 h-3 bg-gray-400 border-2 border-white rounded-full shadow-sm"
          />
          <Handle
            type="source"
            position={Position.Bottom}
            isConnectable={isConnectable}
            className="w-3 h-3 bg-gray-400 border-2 border-white rounded-full shadow-sm"
          />
          <Handle
            type="target"
            position={Position.Left}
            id="left"
            isConnectable={isConnectable}
            className="w-3 h-3 bg-gray-400 border-2 border-white rounded-full shadow-sm"
          />
          <Handle
            type="source"
            position={Position.Right}
            id="right"
            isConnectable={isConnectable}
            className="w-3 h-3 bg-gray-400 border-2 border-white rounded-full shadow-sm"
          />
        </div>
      </HoverCardTrigger>
      
      {/* Debug info hover card */}
      {isDebugMode && (
        <HoverCardContent className="w-auto max-w-[500px] bg-black/90 text-white border-[#6366F1] p-4 overflow-auto max-h-[400px]">
          <div className="font-mono text-xs whitespace-pre">
            <h3 className="text-[#75e6da] mb-2 font-bold">Debug Info:</h3>
            {JSON.stringify(data, null, 2)}
          </div>
        </HoverCardContent>
      )}
    </HoverCard>
  );
}
